/**
 * Inline videos that rest before they play.
 *
 * Ported from the Nouriva theme's `video-player.js`. That theme drove a Swiper carousel; this one
 * scrolls a plain overflow rail, so the track observer is rooted on the rail element instead and
 * there is no duplicated-slide handling to do.
 *
 * Markup contract, all data attributes so the section stays plain HTML:
 *   [data-video-track]  a scrolling container whose videos stop when carried out of view
 *   [data-video-frame]  one video's box — carries the state classes and --progress
 *   [data-video-el]     the <video>
 *   [data-video-play]   play/pause toggle, ships hidden
 *   [data-video-mute]   mute toggle, ships hidden
 *
 * Only one video plays at a time, page-wide.
 */

const PLAYING = 'is-video-playing';
const STARTED = 'is-video-started';

/** @param {Element | null | undefined} frame */
function toggleOf(frame) {
  return frame?.querySelector('[data-video-play]') ?? null;
}

// Tracks already being watched, so a re-render in the theme editor does not stack a second
// observer on the same rail.
const watched = new WeakSet();

/**
 * A muted autoplaying video is scenery, not something the visitor chose to watch — it neither
 * interrupts, nor is stopped by, a video played on purpose.
 *
 * @param {HTMLVideoElement} video
 */
function isScenery(video) {
  return video.autoplay && video.muted;
}

/** @param {HTMLVideoElement} video */
function stop(video) {
  video.pause();
  video.currentTime = 0;
  video.closest('[data-video-frame]')?.classList.remove(PLAYING, STARTED);
}

/** @param {Element} frame */
function start(frame) {
  const video = frame.querySelector('[data-video-el]');
  if (!(video instanceof HTMLVideoElement)) return;

  frame.classList.add(PLAYING);
  video.play().catch(() => {
    // A refused play would leave the frame looking started and silent. Put the poster back so the
    // button is there to try again.
    frame.classList.remove(PLAYING);
  });
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const mute = event.target.closest('[data-video-mute]');
  if (mute) {
    const video = mute.closest('[data-video-frame]')?.querySelector('[data-video-el]');
    if (video instanceof HTMLVideoElement) video.muted = !video.muted;
    return;
  }

  // The whole tile toggles playback, not just the 44px button: on a phone the button is a small
  // target, and while playing it is invisible (it only reappears on hover, which touch lacks).
  // Links inside the tile (the creator handle) keep their own behaviour.
  if (event.target.closest('a')) return;
  const frame = event.target.closest('[data-video-frame]');
  if (!frame || !frame.querySelector('[data-video-play]:not([hidden])')) return;

  const video = frame.querySelector('[data-video-el]');
  if (video instanceof HTMLVideoElement && !video.paused) {
    // Paused where it is rather than stopped: a second press is "hold on", not "start over".
    video.pause();
    return;
  }

  start(frame);
});

/**
 * The frame's state follows the video itself, not the clicks that asked for it, so every way a
 * video can stop — a press, another one starting, the rail carrying it off, the end of the clip —
 * leaves the controls right.
 *
 * @param {Event} event
 */
function syncState(event) {
  const video = event.target;
  if (!(video instanceof HTMLVideoElement) || !video.matches('[data-video-el]')) return;

  const frame = video.closest('[data-video-frame]');
  if (!(frame instanceof HTMLElement)) return;

  const toggle = toggleOf(frame);

  switch (event.type) {
    case 'play':
      frame.classList.add(PLAYING, STARTED);
      toggle?.setAttribute('aria-pressed', 'true');
      break;
    case 'pause':
      frame.classList.remove(PLAYING);
      toggle?.setAttribute('aria-pressed', 'false');
      break;
    case 'ended':
      video.currentTime = 0;
      frame.classList.remove(STARTED);
      break;
    case 'timeupdate':
      frame.style.setProperty('--oudh-video-progress', String(video.duration ? video.currentTime / video.duration : 0));
      break;
    case 'volumechange':
      frame.querySelector('[data-video-mute]')?.setAttribute('aria-pressed', String(video.muted));
      break;
  }
}

// Media events do not bubble, so all of this listens in the capture phase.
for (const type of ['play', 'pause', 'ended', 'timeupdate', 'volumechange']) {
  document.addEventListener(type, syncState, true);
}

document.addEventListener(
  'play',
  (event) => {
    const started = event.target;
    if (!(started instanceof HTMLVideoElement) || !started.matches('[data-video-el]')) return;
    if (isScenery(started)) return;

    for (const other of document.querySelectorAll('[data-video-el]')) {
      if (other instanceof HTMLVideoElement && other !== started && !other.paused && !isScenery(other)) stop(other);
    }
  },
  true
);

/**
 * Two observers, not one: a tile scrolled off the rail needs the rail itself as root, since the
 * page never moved; the whole rail scrolled past the viewport needs the default root, which an
 * element-rooted observer cannot see.
 *
 * @param {Element} track
 */
function watch(track) {
  /** @param {IntersectionObserverEntry[]} entries */
  const leftView = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) continue;
      const video = entry.target.querySelector('[data-video-el]');
      if (video instanceof HTMLVideoElement && !video.paused) stop(video);
    }
  };

  const observers = [
    new IntersectionObserver(leftView, { root: track, threshold: 0.5 }),
    new IntersectionObserver(leftView, { threshold: 0.5 }),
  ];

  for (const frame of track.querySelectorAll('[data-video-frame]')) {
    for (const observer of observers) observer.observe(frame);
  }

  trackObservers.set(track, observers);
}

/** @type {WeakMap<Element, IntersectionObserver[]>} */
const trackObservers = new WeakMap();

/**
 * The controls ship hidden, so a video whose script never arrived falls back to the browser's own
 * player rather than to a poster with a dead button on it. Revealing them here is what makes them
 * real, and is also what takes the native controls away.
 *
 * @param {ParentNode} scope
 */
function reveal(scope) {
  for (const button of scope.querySelectorAll('[data-video-play][hidden], [data-video-mute][hidden]')) {
    if (button instanceof HTMLElement) button.hidden = false;
  }

  for (const video of scope.querySelectorAll('[data-video-el]')) {
    if (video instanceof HTMLVideoElement) video.controls = false;
  }
}

/** @param {ParentNode} scope */
function init(scope) {
  reveal(scope);

  for (const track of scope.querySelectorAll('[data-video-track]')) {
    if (watched.has(track)) continue;
    watched.add(track);
    watch(track);
  }
}

init(document);

// The theme editor replaces a section's markup wholesale when a setting changes, so the new markup
// needs its controls revealed and its rail its own observer, and the old one has to be handed back.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target instanceof Element ? event.target : document);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;
  for (const track of event.target.querySelectorAll('[data-video-track]')) {
    for (const observer of trackObservers.get(track) ?? []) observer.disconnect();
    trackObservers.delete(track);
    watched.delete(track);
  }
});
